'use client';

import { useEffect, useRef, useState } from 'react';

import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import { getMediaBaseUrl } from '@/fo/util/image-loader';

import { styles } from './UiProductInfoVideo.styles';

export interface UiProductInfoVideoProps {
  video?: string;
}

export function UiProductInfoVideo({
  video: initialVideo
}: UiProductInfoVideoProps) {
  const slots = styles();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [video, setVideo] = useState(initialVideo);
  const [showControls, setShowControls] = useState(false);
  const [isInViewport, setIsInViewport] = useState(false);
  const [userPaused, setUserPaused] = useState(false);

  useEffect(() => {
    setVideo(initialVideo);
  }, [initialVideo]);

  useEffect(() => {
    return utilEventConfigurableProduct.onProductSelect((product) => {
      const { video, parentVideo } = product;

      setVideo(video ?? parentVideo);
    });
  }, []);

  // Intersection Observer для отслеживания попадания в viewport
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsInViewport(entry.isIntersecting);
        });
      },
      {
        threshold: 0.1
      }
    );

    observer.observe(videoElement);

    return () => {
      observer.unobserve(videoElement);
    };
  }, [video]);

  // Управление воспроизведением при попадании в viewport
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    if (isInViewport && !userPaused) {
      videoElement.play().catch(() => {
        // Игнорируем ошибки автопроигрывания (например, если браузер блокирует)
      });
    } else if (!isInViewport) {
      videoElement.pause();
    }
  }, [isInViewport, userPaused]);

  const handleMouseEnter = () => {
    setShowControls(true);
  };

  const handleMouseLeave = () => {
    setShowControls(false);
  };

  const handlePause = () => {
    // Если видео поставлено на паузу когда оно в viewport, значит пользователь сделал это сам
    if (isInViewport) {
      setUserPaused(true);
    }
  };

  const handlePlay = () => {
    // Когда пользователь сам запускает видео, сбрасываем флаг паузы
    setUserPaused(false);
  };

  if (!video) {
    return null;
  }

  return (
    <div className={slots.base()}>
      <video
        ref={videoRef}
        src={`${getMediaBaseUrl()}${video}`}
        controls={showControls}
        className={slots.video()}
        playsInline
        muted
        loop
        width={1024}
        height={574}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onPause={handlePause}
        onPlay={handlePlay}
      />
    </div>
  );
}
